// client/src/components/merchant/BrandPicker.js
// Selecteur de marque avec logo, utilise dans la creation ET la modification
// d'annonce.
//
// Pourquoi une liste et non un champ libre : en saisie libre la meme marque
// arrive en base sous cinq orthographes (« Nike », « nike », « NIKE  »), ce qui
// rend impossible tout filtre, regroupement ou affichage de logo.
// L'option « Autre » reste disponible : un marchand ne doit jamais etre bloque
// parce que sa marque n'est pas encore au catalogue.
//
// react-icons, aucun emoji. Maxula

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch, FiChevronDown, FiX, FiCheck, FiTag } from 'react-icons/fi';
import API from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';

// value : { brandId: number|null, brand: string }
const BrandPicker = ({ value, onChange, disabled = false }) => {
  const brandId = value?.brandId || null;
  const brandName = value?.brand || '';

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [freeMode, setFreeMode] = useState(!brandId && !!brandName);
  const boxRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await API.get('/brands');
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (alive) setBrands(list);
      } catch (e) {
        // Catalogue indisponible -> on bascule en saisie libre plutot que de
        // bloquer la creation de l'annonce.
        if (alive) setBrands([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Fermeture au clic exterieur : sans ca, le menu reste ouvert par-dessus le
  // reste du formulaire.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => String(b.name || '').toLowerCase().includes(q));
  }, [brands, query]);

  const selected = useMemo(
    () => brands.find((b) => String(b.id) === String(brandId)) || null,
    [brands, brandId]
  );

  const pick = (b) => {
    onChange({ brandId: b.id, brand: b.name });
    setOpen(false);
    setQuery('');
    setFreeMode(false);
  };

  const clear = () => {
    onChange({ brandId: null, brand: '' });
    setFreeMode(false);
    setQuery('');
  };

  const Logo = ({ brand, size = 28 }) => {
    const src = brand?.logo ? normalizeImagePath(brand.logo) : null;
    if (!src) {
      return (
        <span
          className="inline-flex items-center justify-center rounded bg-gray-100 text-gray-400 shrink-0"
          style={{ width: size, height: size }}
        >
          <FiTag size={Math.round(size * 0.55)} />
        </span>
      );
    }
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        className="rounded object-contain bg-white border border-gray-100 shrink-0"
        style={{ width: size, height: size }}
        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
      />
    );
  };

  // Saisie libre : marque absente du catalogue.
  if (freeMode) {
    return (
      <div>
        <div className="flex gap-2">
          <input
            type="text"
            value={brandName}
            disabled={disabled}
            maxLength={120}
            onChange={(e) => onChange({ brandId: null, brand: e.target.value })}
            placeholder="Nom de la marque"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
          />
          <button
            type="button"
            onClick={() => { setFreeMode(false); onChange({ brandId: null, brand: '' }); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 shrink-0"
          >
            Choisir dans la liste
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Cette marque n'est pas au catalogue : elle sera enregistrée en texte, sans logo.
        </p>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2 border border-gray-300 rounded-lg bg-white text-left hover:bg-gray-50 disabled:opacity-60"
      >
        {selected ? (
          <>
            <Logo brand={selected} />
            <span className="flex-1 truncate text-gray-900">{selected.name}</span>
          </>
        ) : (
          <span className="flex-1 text-gray-400">
            {loading ? 'Chargement des marques…' : 'Choisir une marque'}
          </span>
        )}
        {selected && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); clear(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); clear(); } }}
            className="p-1 text-gray-400 hover:text-red-600"
            aria-label="Retirer la marque"
          >
            <FiX size={16} />
          </span>
        )}
        <FiChevronDown className="text-gray-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher une marque..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-6 text-center text-sm text-gray-500">Chargement…</div>
            ) : filtered.length ? (
              filtered.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => pick(b)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-primary-50 text-left"
                >
                  <Logo brand={b} />
                  <span className="flex-1 truncate text-sm text-gray-900">{b.name}</span>
                  {String(b.id) === String(brandId) && <FiCheck className="text-primary-600" size={16} />}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-gray-500">
                Aucune marque ne correspond
              </div>
            )}
          </div>

          <div className="p-2 border-t">
            <button
              type="button"
              onClick={() => { setFreeMode(true); setOpen(false); onChange({ brandId: null, brand: query.trim() }); }}
              className="w-full px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 rounded-lg text-left"
            >
              Ma marque n'est pas dans la liste — la saisir manuellement
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandPicker;
